// client/src/routes/app/properties/new.tsx — SCR-PROP-002 (create) · PROP-004 (04 §8.3).
//
// `Portfolio.Write`, not `Portfolio.Read`: an accountant can see the portfolio and cannot add
// to it (03 §17.1), and the client mirror has to say so before the API's 403 does — the nav
// hides nothing here, because this URL is reached from a button the same mirror already hid.
//
// Creating lands on the new property's detail rather than back on the list. The landlord's
// next act is almost always to add the co-owners or the units, and both are tabs of the screen
// they are now on — and `POST /api/v1/properties` answers 201 with that very detail, so the
// screen renders from the response without a second GET (PROP-001's mapper says why).

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { PropertyForm } from '@/features/properties/PropertyForm';
import { useCreateProperty } from '@/features/properties/queries';
import { toCreatePropertyRequest, type PropertyFormValues } from '@/features/properties/schema';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/properties/new')({
  component: NewPropertyScreen,
});

function NewPropertyScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateProperty();

  return (
    <RequirePermission permission="Portfolio.Write">
      <PageHeader
        title={t('properties.form.createTitle')}
        description={t('properties.form.createLead')}
        backTo="/app/properties"
        backLabel={t('properties.list.title')}
      />
      <PropertyForm
        submitLabel={t('properties.form.create')}
        onSubmit={async (values: PropertyFormValues) => {
          const created = await create.mutateAsync({ body: toCreatePropertyRequest(values) });
          toast.success(t('properties.form.created'));
          await navigate({ to: '/app/properties/$propertyId', params: { propertyId: created.id } });
        }}
        onCancel={() => {
          void navigate({ to: '/app/properties' });
        }}
      />
    </RequirePermission>
  );
}
